import { useAnalysisStore } from '../../stores/appAnalysisStore';
import { getOperationalPages, hasMainPageRestriction, STAFF_HOME_PATH } from '../../services/accessManagement/mainPageRestriction';
import { useTranslation } from "react-i18next";
import { NavLink, useNavigate } from "react-router-dom";
import { Home, Clock8, Settings, LogOut, Megaphone, DollarSign, Calculator, ChefHat, BarChart3, FileText, ClipboardCheck, UtensilsCrossed, Bell, ChevronRight, ChevronDown, BookOpen, Trash2, Package, TrendingUp, Percent, IdCardLanyard, Target } from 'lucide-react'
import { signOut as supabaseSignOut } from '../../services/authentication/supabaseService';
import { useAuth } from "../../services/authentication/authContext";
import { isInvoiceOnlyAccount, canAccessRecipesPage, canAccessChecklistsPage, canAccessMepPage, canAccessNotificationsPage, canAccessWastePage, canAccessInventoryPage, canAccessReportsPage, canAccessCostPercentPage, canAccessAnalysisPage } from '../../services/accessManagement/accessManagement';
import { Collapse } from './Collapse';
import { useAppUserDefaultStore, saveAppPagesExpandedSetting } from '../../stores/appUserDefaultStore';
import { GuidedToursButton } from '../onboarding/GuidedToursButton';
import { SupportNavLink } from './SupportNavLink';
import { SetupOverviewNavLink } from './SetupOverviewNavLink';


export default function SideBarMenu() {
    const { t } = useTranslation()
    const hasUnseenAnalysis = useAnalysisStore(s => !s.analysisFindingsSeen && Object.values(s.wasteFindings).some(findings => findings.length > 0));
    const navigate = useNavigate();
    const { backendUser, accountSettings } = useAuth();
    const appPagesExpanded = useAppUserDefaultStore(s => s.appPagesExpanded);

    const sidebarMenuClass = `text-tertiary dark:text-tertiary-dark flex py-2 items-center gap-2 p-2 rounded-xl font-medium hover:text-textGreen dark:hover:text-text-dark hover:bg-cardLightGreen/80 dark:hover:bg-cardLightGreen-dark/80`
    const sidebarMenuActiveClass = 'bg-cardLightGreen dark:bg-cardLightGreen-dark text-textGreen dark:text-text-dark'
    
    const handleLogout = async () => {
    try {
        await supabaseSignOut();
        navigate('/login', { replace: true });
    } catch (err) {
        console.error('Logout error', err);
    }
    };

    const handleToggleAppPages = async () => {
      try {
        await saveAppPagesExpandedSetting(!appPagesExpanded);
      } catch (error) {
        console.error('Failed to save app pages expanded setting');
      }
    };

    if (!backendUser) return null;

    if (hasMainPageRestriction(backendUser)) {
      const pages = getOperationalPages(backendUser, accountSettings);
      const icons: Record<string, typeof Home> = { '/recipes': ChefHat, '/inventory': Package, '/mep': UtensilsCrossed, '/checklists': ClipboardCheck, '/notifications': Bell, '/waste': Trash2, '/staff': IdCardLanyard };
      return (
        <aside className="w-[228px] bg-background dark:bg-backgroundNav-dark h-full overflow-y-auto">
          <div className="flex flex-col h-full p-4">
            <ul className="flex flex-col space-y-2">
              {(pages.length ? pages : [{ path: STAFF_HOME_PATH, label: 'nav.homePage' }]).map(page => {
                const Icon = icons[page.path] ?? Home;
                return <li key={page.path}><NavLink to={page.path} className={({ isActive }) => `${sidebarMenuClass} ${isActive ? sidebarMenuActiveClass : ''}`}><Icon className="w-6 h-6" />{t(page.label)}</NavLink></li>;
              })}
            </ul>
            <ul className="flex flex-col space-y-2 mt-auto">
              <GuidedToursButton />
              <SetupOverviewNavLink listItem className={sidebarMenuClass} activeClassName={sidebarMenuActiveClass} />
              <li><SupportNavLink className={sidebarMenuClass} activeClassName={sidebarMenuActiveClass} /></li>
              <li><button onClick={handleLogout} className={`${sidebarMenuClass} w-full text-left`}><LogOut className="w-6 h-6" />{t('nav.logout')}</button></li>
            </ul>
          </div>
        </aside>
      );
    }

    // Invoice-only accounts get a deliberately minimal nav — Home / Upload invoices /
    // Orders, plus Settings (admin) + Logout. Returned early so no other account
    // setting can add an item, including the ones forced on while impersonating.
    if (isInvoiceOnlyAccount(backendUser, accountSettings)) {
      return (
        <aside className="w-[228px] bg-background dark:bg-backgroundNav-dark h-full overflow-y-auto">
          <div className="flex flex-col h-full p-4">
            <ul className="flex flex-col space-y-2">
              <li>
                <NavLink
                  to="/home"
                  className={({ isActive }) =>
                    `${sidebarMenuClass} ${isActive ? sidebarMenuActiveClass : ''}`
                  }
                >
                  <Home className="w-6 h-6" />
                  {t('nav.homePage')}
                </NavLink>
              </li>
              <li>
                <NavLink
                  to="/invoices"
                  className={({ isActive }) =>
                    `${sidebarMenuClass} ${isActive ? sidebarMenuActiveClass : ''}`
                  }
                >
                  <FileText className="w-6 h-6" />
                  {t('nav.upload', { defaultValue: 'Upload' })}
                </NavLink>
              </li>
              <li>
                <NavLink
                  to="/orderhistory/orders"
                  className={({ isActive }) =>
                    `${sidebarMenuClass} ${isActive ? sidebarMenuActiveClass : ''}`
                  }
                >
                  <Clock8 className="w-6 h-6" />
                  {t('nav.orderHistory')}
                </NavLink>
              </li>
            </ul>

            <ul className="flex flex-col space-y-2 mt-auto">
              <SetupOverviewNavLink listItem className={sidebarMenuClass} activeClassName={sidebarMenuActiveClass} />
              <li><SupportNavLink className={sidebarMenuClass} activeClassName={sidebarMenuActiveClass} /></li>
              {backendUser.role === 'admin' && (
                <li>
                  <NavLink
                    to="/settings"
                    className={({ isActive }) =>
                      `${sidebarMenuClass} ${isActive ? sidebarMenuActiveClass : ''}`
                    }
                  >
                    <Settings className="w-6 h-6" />
                    {t('nav.settings')}
                  </NavLink>
                </li>
              )}
              <li>
                <button
                  onClick={() => {
                    handleLogout()
                  }}
                  className={`${sidebarMenuClass} w-full text-left`}
                >
                  <LogOut className="w-6 h-6" />
                  {t('nav.logout')}
                </button>
              </li>
            </ul>
          </div>
        </aside>
      );
    }

    // Check user access for special features
    const hasRecipesAccess = canAccessRecipesPage(backendUser, accountSettings);
    const hasChecklistsAccess = canAccessChecklistsPage(backendUser, accountSettings);
    const hasMepAccess = canAccessMepPage(backendUser, accountSettings);
    const hasNotificationsAccess = canAccessNotificationsPage(backendUser, accountSettings);
    const hasWasteAccess = canAccessWastePage(backendUser, accountSettings);
    const hasInventoryAccess = canAccessInventoryPage(backendUser, accountSettings);
    const hasReportsAccess = canAccessReportsPage(backendUser, accountSettings);
    const hasCostPercentAccess = canAccessCostPercentPage(backendUser, accountSettings);
    const hasAnalysisAccess = canAccessAnalysisPage(backendUser, accountSettings);

    return (
      <aside className="w-[228px] bg-background dark:bg-backgroundNav-dark h-full overflow-y-auto">
        <div className="flex flex-col h-full p-4">
          {/* Top menu items */}
          <ul className="flex flex-col space-y-2">
            <li>
              <NavLink
                to="/dashboard"
                className={({ isActive }) =>
                  `${sidebarMenuClass} ${isActive ? sidebarMenuActiveClass : ''}`
                }
              >
                <Home className="w-6 h-6" />
                {t('nav.home')}
              </NavLink>
            </li>
            {/* Search removed for now (not supported anymore) */}
            {/* <li>
              <NavLink
                to="/search"
                className={({ isActive }) =>
                  `${sidebarMenuClass} ${isActive ? sidebarMenuActiveClass : ''}`
                }
              >
                <Search className="w-6 h-6" />
                {t('nav.search')}
              </NavLink>
            </li> */}
            {/* Import removed for now (not supported anymore) */}
            {/* <li>
              <NavLink
                to="/import"
                className={({ isActive }) =>
                  `${sidebarMenuClass} ${isActive ? sidebarMenuActiveClass : ''}`
                }
              >
                <Import className="w-6 h-6" />
                {t('nav.import')}
              </NavLink>
            </li> */}
            {/* Products removed for now (no use for this tab yet) */}
            {/* <li>
              <NavLink
                to="/products"
                className={({ isActive }) =>
                  `${sidebarMenuClass} ${isActive ? sidebarMenuActiveClass : ''}`
                }
              >
                <Package className="w-6 h-6" />
                {t('nav.products')}
              </NavLink>
            </li> */}
            {/* Reports removed for now (not supported anymore) */}
            {/* <li>
              <NavLink
                to="/reports"
                className={({ isActive }) =>
                  `${sidebarMenuClass} ${isActive ? sidebarMenuActiveClass : ''}`
                }
              >
                <TrendingUp className="w-6 h-6" />
                {t('nav.reports')}
              </NavLink>
            </li> */}
            <li>
              <NavLink
                to="/revenuehistory"
                className={({ isActive }) =>
                  `${sidebarMenuClass} ${isActive ? sidebarMenuActiveClass : ''}`
                }
              >
                <DollarSign className="w-6 h-6" />
                {t('nav.revenueHistory')}
              </NavLink>
            </li>
            <li>
              <NavLink
                to="/orderhistory"
                className={({ isActive }) =>
                  `${sidebarMenuClass} ${isActive ? sidebarMenuActiveClass : ''}`
                }
              >
                <Clock8 className="w-6 h-6" />
                {t('nav.orderHistory')}
              </NavLink>
            </li>
            {hasCostPercentAccess && (
              <li>
                <NavLink
                  to="/costpercent"
                  className={({ isActive }) =>
                    `${sidebarMenuClass} ${isActive ? sidebarMenuActiveClass : ''}`
                  }
                >
                  <Percent className="w-6 h-6" />
                  {t('nav.costPercent')}
                </NavLink>
              </li>
            )}
            {hasAnalysisAccess && (
              <li>
                <NavLink
                  to="/analysis"
                  className={({ isActive }) =>
                    `${sidebarMenuClass} ${isActive ? sidebarMenuActiveClass : ''}`
                  }
                >
                  <Target className="w-6 h-6" />
                  {t('analysis.navLabel')}
                  {hasUnseenAnalysis && <span className="ml-auto rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 text-xs font-semibold min-w-5 h-5 px-1 flex items-center justify-center">1</span>}
                </NavLink>
              </li>
            )}
            <li>
              <NavLink
                to="/pricealerts"
                className={({ isActive }) =>
                  `${sidebarMenuClass} ${isActive ? sidebarMenuActiveClass : ''}`
                }
              >
                <Megaphone className="w-6 h-6" />
                {t('nav.priceAlerts')}
              </NavLink>
            </li>
            {/* {isAllRestaurants && (
              <li>
                <NavLink
                  to="/crossrestaurantprices"
                  className={({ isActive }) =>
                    `${sidebarMenuClass} ${isActive ? sidebarMenuActiveClass : ''}`
                  }
                >
                  <GitCompare className="w-6 h-6" />
                  {t('nav.crossRestaurantPrices')}
                </NavLink>
              </li>
            )} */}
            <li>
              <NavLink
                to="/costprice"
                className={({ isActive }) =>
                  `${sidebarMenuClass} ${isActive ? sidebarMenuActiveClass : ''}`
                }
              >
                <Calculator className="w-6 h-6" />
                {t('nav.costPrice')}
              </NavLink>
            </li>
            <li>
              <NavLink
                to="/utilization"
                className={({ isActive }) =>
                  `${sidebarMenuClass} ${isActive ? sidebarMenuActiveClass : ''}`
                }
              >
                <Trash2 className="w-6 h-6" />
                {t('nav.productUtilization')}
              </NavLink>
            </li>
            <li>
              <NavLink
                to="/menus"
                className={({ isActive }) =>
                  `${sidebarMenuClass} ${isActive ? sidebarMenuActiveClass : ''}`
                }
              >
                <BookOpen className="w-6 h-6" />
                {t('nav.menus')}
              </NavLink>
            </li>
            <li>
              <NavLink
                to="/charts"
                className={({ isActive }) =>
                  `${sidebarMenuClass} ${isActive ? sidebarMenuActiveClass : ''}`
                }
              >
                <BarChart3 className="w-6 h-6" />
                {t('nav.charts')}
              </NavLink>
            </li>
            {hasReportsAccess && (
              <li>
                <NavLink
                  to="/reports"
                  className={({ isActive }) =>
                    `${sidebarMenuClass} ${isActive ? sidebarMenuActiveClass : ''}`
                  }
                >
                  <TrendingUp className="w-6 h-6" />
                  {t('nav.reports')}
                </NavLink>
              </li>
            )}
          </ul>

          {/* App Pages collapsible section */}
          {(hasRecipesAccess || hasChecklistsAccess || hasMepAccess || hasNotificationsAccess || hasWasteAccess || hasInventoryAccess) && (
            <div className="mt-4">
              <button
                onClick={handleToggleAppPages}
                className="w-full flex items-center gap-1 px-2 py-1 text-xs font-semibold uppercase tracking-wider text-muted dark:text-muted-dark hover:text-tertiary dark:hover:text-tertiary-dark"
              >
                {t('nav.appPages')}
                {appPagesExpanded ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </button>
              <Collapse open={appPagesExpanded}>
                <ul className="flex flex-col space-y-2 pl-4 mt-2">
                  {hasRecipesAccess && (
                    <li>
                      <NavLink
                        to="/recipes"
                        className={({ isActive }) =>
                          `${sidebarMenuClass} ${isActive ? sidebarMenuActiveClass : ''}`
                        }
                      >
                        <ChefHat className="w-6 h-6" />
                        {t('nav.recipes')}
                      </NavLink>
                    </li>
                  )}
                  {hasChecklistsAccess && (
                    <li>
                      <NavLink
                        to="/checklists"
                        className={({ isActive }) =>
                          `${sidebarMenuClass} ${isActive ? sidebarMenuActiveClass : ''}`
                        }
                      >
                        <ClipboardCheck className="w-6 h-6" />
                        {t('nav.checklists')}
                      </NavLink>
                    </li>
                  )}
                  {hasMepAccess && (
                    <li>
                      <NavLink
                        to="/mep"
                        className={({ isActive }) =>
                          `${sidebarMenuClass} ${isActive ? sidebarMenuActiveClass : ''}`
                        }
                      >
                        <UtensilsCrossed className="w-6 h-6" />
                        {t('nav.mep')}
                      </NavLink>
                    </li>
                  )}
                  {hasInventoryAccess && (
                    <li>
                      <NavLink
                        to="/inventory"
                        className={({ isActive }) =>
                          `${sidebarMenuClass} ${isActive ? sidebarMenuActiveClass : ''}`
                        }
                      >
                        <Package className="w-6 h-6" />
                        {t('nav.inventory')}
                      </NavLink>
                    </li>
                  )}
                  {hasNotificationsAccess && (
                    <li>
                      <NavLink
                        to="/notifications"
                        className={({ isActive }) =>
                          `${sidebarMenuClass} ${isActive ? sidebarMenuActiveClass : ''}`
                        }
                      >
                        <Bell className="w-6 h-6" />
                        {t('nav.notifications')}
                      </NavLink>
                    </li>
                  )}
                  {hasWasteAccess && (
                    <li>
                      <NavLink
                        to="/waste"
                        className={({ isActive }) =>
                          `${sidebarMenuClass} ${isActive ? sidebarMenuActiveClass : ''}`
                        }
                      >
                        <Trash2 className="w-6 h-6" />
                        {t('nav.wasteTracking')}
                      </NavLink>
                    </li>
                  )}
                </ul>
              </Collapse>
            </div>
          )}

          {/* Bottom menu items */}
          <ul className="flex flex-col space-y-2 mt-auto">
            <GuidedToursButton />
            <SetupOverviewNavLink listItem className={sidebarMenuClass} activeClassName={sidebarMenuActiveClass} />
            <li><SupportNavLink className={sidebarMenuClass} activeClassName={sidebarMenuActiveClass} /></li>
            {backendUser.role === 'admin' && (
              <li>
                <NavLink
                  to="/settings"
                  className={({ isActive }) =>
                    `${sidebarMenuClass} ${isActive ? sidebarMenuActiveClass : ''}`
                  }
                >
                  <Settings className="w-6 h-6" />
                  {t('nav.settings')}
                </NavLink>
              </li>
            )}
            
            <li>
                <button 
                onClick={() => {
                    handleLogout()
                }}
                className={`${sidebarMenuClass} w-full text-left`}
                >
                    <LogOut className="w-6 h-6" />
                    {t('nav.logout')}
                </button>
            </li>
          </ul>
        </div>
      </aside>
    );
}
